import { activityTracker } from '../utils/activityTracker';

export type StartupLocation =
  | { lat: number; lng: number; zoom?: number }
  | 'gps'
  | null;

const LAST_MAP_VIEW_KEY = 'lastMapView';

const PREDEFINED_LOCATIONS: Record<string, { lat: number; lng: number; zoom: number } | 'gps'> = {
  paris: { lat: 48.8566, lng: 2.3522, zoom: 12 },
  'las vegas': { lat: 36.1699, lng: -115.1398, zoom: 12 },
  pyongyang: { lat: 39.0392, lng: 125.7625, zoom: 12 },
  'your location': 'gps',
};

export function getPredefinedStartupLocation(value: unknown): StartupLocation {
  if (typeof value !== 'string') return null;
  return PREDEFINED_LOCATIONS[value.trim().toLowerCase()] || null;
}

export function isValidLatLng(lat: unknown, lng: unknown): lat is number {
  return lat != null &&
    lng != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(Number(lat)) <= 90 &&
    Math.abs(Number(lng)) <= 180;
}

export function getStartupLocationFromUrlOrStorage(): StartupLocation {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const selectedLocationParam = params.get('selectedLocation');
  const coordsParam = params.get('coords');

  const predefinedLocation = getPredefinedStartupLocation(selectedLocationParam);
  if (predefinedLocation) {
    return predefinedLocation;
  }

  if (coordsParam) {
    const [lat, lng] = coordsParam.split(',').map(Number);
    if (isValidLatLng(lat, lng)) {
      return { lat, lng, zoom: 12 };
    }
  }

  try {
    const rawStoredView = localStorage.getItem(LAST_MAP_VIEW_KEY);
    if (!rawStoredView) return null;

    const parsed = JSON.parse(rawStoredView);
    const lat = Number(parsed?.lat);
    const lng = Number(parsed?.lng);
    const zoom = Number(parsed?.zoom);

    if (isValidLatLng(lat, lng) && Number.isFinite(zoom)) {
      return { lat, lng, zoom };
    }
  } catch (error) {
    console.warn('[Startup] Failed to parse stored map view:', error);
  }

  return null;
}

export function cleanupStartupUrlParameters() {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const selectedLocationParam = params.get('selectedLocation');
  const coordsParam = params.get('coords');
  const showLandingParam = params.get('showlanding');

  if (selectedLocationParam || coordsParam || showLandingParam) {
    const newUrl = new URL(window.location.href);
    if (selectedLocationParam) newUrl.searchParams.delete('selectedLocation');
    if (coordsParam) newUrl.searchParams.delete('coords');
    if (showLandingParam) newUrl.searchParams.delete('showlanding');
    window.history.replaceState({}, '', newUrl);
  }
}

export function getInitialWelcomeScreenState() {
  if (typeof window === 'undefined') return false;

  const params = new URLSearchParams(window.location.search);
  if (params.get('showWelcome') === 'true' || params.get('showWelcome') === '1') {
    return true;
  }

  if (params.get('selectedLocation') || params.get('coords')) {
    return false;
  }

  return activityTracker.shouldShowLandingPage();
}

export function ensureThemeColour(color: string) {
  if (typeof document === 'undefined') return;

  let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = color;
}
